import React, { memo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { FormattedNumberPropType } from '../../../prop-types/intl';
import { MessagePropType } from '../../../prop-types/message';
import Global from '../../../store/global-selectors';
import { forceAddToBatch } from '../../../store/batches/batches.actions';

import style from './style.module.less';

export const messages = defineMessages({
  excludeMessage: {
    description: 'Rejected fulfillmentPlans exclude growler message',
    defaultMessage: 'No, exclude {count, plural, one {this shipment} other {these shipments}}',
    id: '9Wv+cl',
  },
  confirmMessage: {
    description: 'Rejected fulfillmentPlans confirm growler message',
    defaultMessage: 'Yes, move {count, plural, one {this shipment} other {these shipments}}',
    id: '3Hz1FR',
  },
  headerMessage: {
    description: 'Rejected fulfillmentPlans modal header message',
    defaultMessage: 'Move {count, plural, one {Shipment} other {Shipments}} to Batch: {destinationBatchName}',
    id: '8TZEr3',
  },
  bodyMessage: {
    description: 'Rejected fulfillmentPlans modal body message',
    defaultMessage:
      '{count, plural, one {# shipment is} other {# shipments are}} already in a different batch. Are you sure you want to move {count, plural, one {it} other {them}} to Batch: {destinationBatchName}?',
    id: 'OdLzbX',
  },
});

const BatchRejectedFulfillmentPlanModalComponent = ({
  modalFrameworkMethods,
  useFulfillmentPlans = false,
  destinationFulfillmentPlanSetId = '',
  errorMessage,
  successMessage,
}) => {
  const dispatch = useDispatch();
  const rejectedSalesOrders = useSelector(Global.selectBatchCreateWithSalesOrdersRejectedFullfillmentPlans);
  const rejectedFulfillmentPlans = useSelector(Global.selectBatchCreateWithFulfillmentPlansRejectedFullfillmentPlans) || [];
  const rejectedPlans = useFulfillmentPlans ? rejectedFulfillmentPlans : rejectedSalesOrders;
  const count = useFulfillmentPlans ? rejectedFulfillmentPlans.length : rejectedSalesOrders.length;
  const { name: destinationBatchName, fulfillmentPlanSetId } = useSelector((state) =>
    Global.selectBatchesNavNavBatchByFulfillmentPlanSetId(state, {
      fulfillmentPlanSetId: destinationFulfillmentPlanSetId,
    }),
  );

  const { cancelModal, closeModal } = modalFrameworkMethods;

  const onConfirmClick = useCallback(() => {
    const fulfillmentPlanIds = rejectedPlans.map(({ fulfillmentPlanId }) => fulfillmentPlanId);
    dispatch(forceAddToBatch(fulfillmentPlanSetId, fulfillmentPlanIds, errorMessage, successMessage));
    closeModal();
  }, [closeModal, dispatch, errorMessage, fulfillmentPlanSetId, rejectedPlans, successMessage]);

  return (
    <Modal>
      <ModalHeader>
        <FormattedMessage {...messages.headerMessage} values={{ destinationBatchName, count }} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        <FormattedMessage {...messages.bodyMessage} values={{ count, destinationBatchName }} />
      </ModalBody>
      <ModalFooter className={style['modal-footer']}>
        <Button onClick={onConfirmClick} theme='primary'>
          <FormattedMessage {...messages.confirmMessage} values={{ count }} />
        </Button>
        <Button onClick={cancelModal} theme='tertiary'>
          <FormattedMessage {...messages.excludeMessage} values={{ count }} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

BatchRejectedFulfillmentPlanModalComponent.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  useFulfillmentPlans: PropTypes.bool,
  destinationFulfillmentPlanSetId: PropTypes.string,
  errorMessage: PropTypes.shape({
    title: MessagePropType,
    content: MessagePropType,
    values: PropTypes.shape({
      shipmentCount: FormattedNumberPropType,
      shipmentNumber: FormattedNumberPropType,
      batchName: PropTypes.string,
    }),
  }),
  successMessage: PropTypes.shape({
    title: MessagePropType,
    content: MessagePropType,
    values: PropTypes.shape({
      shipmentCount: FormattedNumberPropType,
      shipmentNumber: FormattedNumberPropType,
      batchName: PropTypes.string,
    }),
  }),
};

export const BatchRejectedFulfillmentPlanModal = memo(BatchRejectedFulfillmentPlanModalComponent);
